import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Moon,
  Sun,
  Globe,
  Shield,
  Menu,
  X,
  List,
  Settings,
  LayoutDashboard,
  ShieldAlert,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const translations = {
    tr: {
      tools: 'Araçlar',
      home: 'Ana Sayfa',
      features: 'Özellikler',
      ipList: 'IP Listesi',
      setup: 'Kurulum',
      stats: 'İstatistikler',
      login: 'Portal',
      map: 'Tehdit Haritası',
      cve: 'CVE/KEV',
      sponsors: 'Sponsorlar',
    },
    en: {
      tools: 'Tools',
      home: 'Home',
      features: 'Features',
      ipList: 'IP List',
      setup: 'Setup',
      stats: 'Statistics',
      login: 'Portal',
      map: 'Threat Map',
      cve: 'CVE/KEV',
      sponsors: 'Sponsors',
    },
  };

  const txt = translations[language] || translations.en;

  const navItems = [
    { path: '/ip-list', label: txt.ipList, icon: List, testId: 'ip-list-nav-link' },
    { path: '/map', label: txt.map, icon: Globe, testId: 'map-nav-link' },
    { path: '/cve', label: txt.cve, icon: ShieldAlert, testId: 'cve-nav-link' },
    { path: '/setup', label: txt.setup, icon: Settings, testId: 'setup-nav-link' },
  ];

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-border bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70"
      data-testid="header"
    >
      {/* Top ticker strip — SOC vibe */}
      <div className="hidden md:block border-b border-border/60 bg-muted/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-7 items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <span className="status-dot" />
                <span>SEC-OPS · LIVE</span>
              </span>
              <span className="hidden lg:inline text-border">|</span>
              <span className="hidden lg:inline">
                FEED <span className="text-primary">OPERATIONAL</span>
              </span>
              <span className="hidden lg:inline text-border">|</span>
              <span className="hidden lg:inline">
                COMMUNITY-POWERED <span className="text-foreground">THREAT INTELLIGENCE</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span>GLOBAL · COLLECTIVE DEFENSE · THREAT.LIVE</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group" data-testid="logo">
            <div className="relative">
              <div className="relative bg-primary/10 border border-primary/40 p-2 sharp group-hover:bg-primary/15 transition-colors">
                <Shield className="h-5 w-5 text-primary" strokeWidth={2.5} />
              </div>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-base font-bold tracking-tight text-foreground">
                {t('brand')}
              </span>
              <span className="mt-1 text-[9px] font-mono text-muted-foreground tracking-[0.22em] uppercase hidden sm:block">
                {t('tagline')}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-0 flex-1 justify-center">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path} data-testid={item.testId}>
                  <button
                    className={`relative h-9 px-3 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.14em] transition-colors
                      ${active
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'}
                    `}
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                    {item.label}
                    {active && (
                      <span className="absolute inset-x-2 -bottom-[17px] h-px bg-primary" />
                    )}
                  </button>
                </Link>
              );
            })}
          </nav>

          {/* Controls & Auth */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-2.5 gap-1.5 font-mono text-[11px] uppercase tracking-wider sharp"
                  data-testid="language-toggle"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {language.toUpperCase()}
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="sharp font-mono">
                <DropdownMenuItem onClick={() => setLanguage('tr')} data-testid="lang-tr" className="text-xs uppercase tracking-wider">
                  <span className="mr-2">TR</span> Türkçe
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en" className="text-xs uppercase tracking-wider">
                  <span className="mr-2">EN</span> English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="h-9 w-9 p-0 sharp"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 text-slate-700" />
              )}
            </Button>

            <div className="h-6 w-px bg-border mx-1" />

            {/* Portal CTA */}
            <Link to="/login">
              <Button
                size="sm"
                className="h-9 gap-2 sharp font-mono text-[11px] uppercase tracking-[0.14em] bg-primary text-primary-foreground hover:bg-primary/90 border border-primary"
              >
                <LayoutDashboard className="h-3.5 w-3.5" />
                {txt.login}
              </Button>
            </Link>
          </div>

          {/* Mobile Controls */}
          <div className="flex md:hidden items-center gap-1 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0 sharp">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="sharp">
                <DropdownMenuItem onClick={() => setLanguage('tr')}>
                  <span className="mr-2">TR</span> Türkçe
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')}>
                  <span className="mr-2">EN</span> English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="h-9 w-9 p-0 sharp"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 text-slate-700" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 sharp"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-xs font-mono uppercase tracking-[0.14em] transition-colors py-2.5 px-3 border-l-2 flex items-center gap-2
                      ${active
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'}
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}

              <div className="border-t border-border mt-3 pt-3">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    size="sm"
                    className="w-full sharp h-10 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-[11px] uppercase tracking-[0.14em]"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    {txt.login}
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
